import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Briefcase, Building2, ChevronDown, ChevronRight, Factory, Hammer, LayoutGrid, Mail, Quote, Shapes, User, Users } from "lucide-react";
import { ICONS, SERVICES } from "@/data/services";
import { CASES, ALL_CASES, CASE_CATEGORIES } from "@/data/caseStudies";
import { scrollToId, EASE } from "@/lib/motion";

const bySlug = Object.fromEntries(ALL_CASES.map((c) => [c.slug, c]));

const CATEGORY_ICONS = {
  "home-services": Hammer,
  "real-estate": Building2,
  b2b: Factory,
  other: Shapes,
};

const NAV_MAIN = [
  { id: "blog", label: "Blog & Resources", route: "/blog" },
  { id: "faq", label: "FAQ", route: "/faq" },
];

const MOBILE_MAIN = [
  { id: "blog", label: "Blog & Resources", route: "/blog" },
  { id: "faq", label: "FAQ", route: "/faq" },
  { id: "contact", label: "Contact", route: "/contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 450);
    } else {
      scrollToId(id);
    }
  };

  const goTop = (e) => {
    e.preventDefault();
    setOpen(false);
    if (pathname !== "/") {
      navigate("/");
    } else if (window.__lenis) {
      window.__lenis.scrollTo(0, { duration: 1.2 });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const close = () => setOpen(false);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-slate-200 py-3" : "py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-12">
        <a
          href="/"
          data-testid="nav-logo-link"
          onClick={goTop}
          className="block"
        >
          <img
            src="/img/logo-nav.png"
            alt="ZD Design"
            className="w-[218px] h-auto"
          />
        </a>

        <nav className="hidden items-center gap-8 md:flex lg:ml-10">
          <div className="group relative">
            <button
              data-testid="nav-about-dropdown"
              className="flex items-center gap-1.5 font-mono text-sm font-medium uppercase tracking-[0.05em] text-mist transition-colors hover:text-flame"
            >
              About
              <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <div className="pointer-events-none absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 translate-y-2 pt-4 opacity-0 transition-all duration-300 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100">
              <div className="glass rounded-2xl border border-slate-200 p-2">
                <Link
                  to="/about"
                  data-testid="nav-about-story"
                  onClick={close}
                  className="flex items-start gap-3 rounded-xl px-4 py-3 transition-colors hover:bg-slate-50"
                >
                  <Users className="mt-0.5 h-4 w-4 shrink-0 text-flame" />
                  <span>
                    <span className="block text-base font-semibold text-slate-900">
                      Our Story
                    </span>
                    <span className="mt-0.5 block text-[15px] text-faint">
                      The studio since 2006
                    </span>
                  </span>
                </Link>
                <Link
                  to="/todd"
                  data-testid="nav-about-todd"
                  onClick={close}
                  className="flex items-start gap-3 rounded-xl px-4 py-3 transition-colors hover:bg-slate-50"
                >
                  <User className="mt-0.5 h-4 w-4 shrink-0 text-flame" />
                  <span>
                    <span className="block text-base font-semibold text-slate-900">
                      Todd
                    </span>
                    <span className="mt-0.5 block text-[15px] text-faint">
                      Founder & Chief Strategist
                    </span>
                  </span>
                </Link>
              </div>
            </div>
          </div>

          <div className="group relative">
            <button
              data-testid="nav-services-dropdown"
              className="flex items-center gap-1.5 font-mono text-sm font-medium uppercase tracking-[0.05em] text-mist transition-colors hover:text-flame"
            >
              Services
              <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <div className="pointer-events-none absolute left-1/2 top-full z-50 w-80 -translate-x-1/2 translate-y-2 pt-4 opacity-0 transition-all duration-300 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100">
              <div className="glass rounded-2xl border border-slate-200 p-2">
                <Link
                  to="/packages"
                  data-testid="nav-packages"
                  onClick={close}
                  className="flex items-start gap-3 rounded-xl bg-flame/5 px-4 py-3 transition-colors hover:bg-slate-50"
                >
                  <LayoutGrid className="mt-0.5 h-4 w-4 shrink-0 text-flame" />
                  <span>
                    <span className="block text-base font-semibold text-slate-900">
                      Packages
                    </span>
                    <span className="mt-0.5 block text-[15px] text-faint">
                      All core offers at a glance
                    </span>
                  </span>
                </Link>
                {SERVICES.map((s) => {
                  const Icon = ICONS[s.icon];
                  return (
                    <Link
                      key={s.slug}
                      to={s.route || `/${s.slug}`}
                      data-testid={`nav-service-${s.slug}`}
                      onClick={close}
                      className="flex items-start gap-3 rounded-xl px-4 py-3 transition-colors hover:bg-slate-50"
                    >
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-flame" />
                      <span>
                        <span className="block text-base font-semibold text-slate-900">
                          {s.title}
                        </span>
                        <span className="mt-0.5 block text-[15px] text-faint">
                          {s.tagline}
                        </span>
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="group relative">
            <button
              data-testid="nav-results-dropdown"
              onClick={goSection("results")}
              className="flex items-center gap-1.5 font-mono text-sm font-medium uppercase tracking-[0.05em] text-mist transition-colors hover:text-flame"
            >
              Results
              <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <div className="pointer-events-none absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 translate-y-2 pt-4 opacity-0 transition-all duration-300 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100">
              <div className="glass rounded-2xl border border-slate-200 p-2">
                <Link
                  to="/testimonials"
                  data-testid="nav-results-testimonials"
                  onClick={close}
                  className="flex items-start gap-3 rounded-xl px-4 py-3 transition-colors hover:bg-slate-50"
                >
                  <Quote className="mt-0.5 h-4 w-4 shrink-0 text-flame" />
                  <span>
                    <span className="block text-base font-semibold text-slate-900">
                      Client Testimonials
                    </span>
                    <span className="mt-0.5 block text-[15px] text-faint">
                      Every client word, lightly polished
                    </span>
                  </span>
                </Link>
                <div className="group/cases relative">
                  <div
                    data-testid="nav-results-cases"
                    className="flex w-full cursor-pointer items-center justify-between rounded-xl px-4 py-3 transition-colors hover:bg-slate-50"
                  >
                    <span className="flex items-start gap-3">
                      <Briefcase className="mt-0.5 h-4 w-4 shrink-0 text-flame" />
                      <span>
                        <span className="block text-base font-semibold text-slate-900">
                          Case Studies
                        </span>
                        <span className="mt-0.5 block text-[15px] text-faint">
                          Nineteen client wins
                        </span>
                      </span>
                    </span>
                    <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-faint transition-transform duration-300 group-hover/cases:-rotate-90" />
                  </div>
                  <div className="pointer-events-none absolute left-full top-0 z-50 py-3 pl-3 opacity-0 transition-all duration-300 group-hover/cases:pointer-events-auto group-hover/cases:opacity-100">
                    <div className="glass w-72 rounded-2xl border border-slate-200 p-2">
                      <Link
                        to="/case-studies"
                        data-testid="nav-case-studies-all"
                        onClick={close}
                        className="flex items-start gap-3 rounded-xl bg-flame/5 px-4 py-3 transition-colors hover:bg-flame/10"
                      >
                        <LayoutGrid className="mt-0.5 h-4 w-4 shrink-0 text-flame" />
                        <span>
                          <span className="block text-base font-semibold text-flame">
                            All Case Studies
                          </span>
                          <span className="mt-0.5 block text-[15px] text-faint">
                            Every client win on one page
                          </span>
                        </span>
                      </Link>
                      <div className="mx-2 my-1 border-t border-slate-100" />
                      {CASE_CATEGORIES.map((cat) => {
                        const CatIcon = CATEGORY_ICONS[cat.id];
                        return (
                          <div key={cat.id} className="group/cat relative">
                            <div
                              data-testid={`nav-case-cat-${cat.id}`}
                              className="flex w-full cursor-pointer items-start gap-3 rounded-xl px-4 py-2.5 transition-colors hover:bg-slate-50"
                            >
                              <CatIcon className="mt-0.5 h-4 w-4 shrink-0 text-flame" />
                              <span className="pr-5">
                                <span className="block text-base font-semibold text-slate-900">
                                  {cat.label}
                                </span>
                                <span className="mt-0.5 block text-[13px] text-faint">
                                  {cat.hint}
                                </span>
                              </span>
                              <ChevronRight className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 shrink-0 text-faint transition-transform duration-300 group-hover/cat:rotate-90" />
                            </div>
                            <div className={`pointer-events-none absolute left-full top-1/2 z-50 py-3 pl-3 ${cat.align === "high" ? "-translate-y-[58%]" : "-translate-y-1/2"} opacity-0 transition-all duration-300 group-hover/cat:pointer-events-auto group-hover/cat:opacity-100`}>
                              <div className="glass max-h-[calc(100vh-18rem)] w-64 overflow-y-auto rounded-2xl border border-slate-200 p-2">
                                {cat.slugs.map((slug) => {
                                  const c = bySlug[slug];
                                  return (
                                    <Link
                                      key={slug}
                                      to={`/case-studies/${slug}`}
                                      data-testid={`nav-all-case-${slug}`}
                                      onClick={close}
                                      className="flex items-start gap-3 rounded-xl px-4 py-2.5 transition-colors hover:bg-slate-50"
                                    >
                                      <Briefcase className="mt-0.5 h-4 w-4 shrink-0 text-flame" />
                                      <span>
                                        <span className="block text-base font-semibold text-slate-900">
                                          {c.client}
                                        </span>
                                        <span className="mt-0.5 block text-[13px] text-faint">
                                          {c.industry}
                                        </span>
                                      </span>
                                    </Link>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {NAV_MAIN.map((l) => (
            <Link
              key={l.id}
              to={l.route}
              data-testid={`nav-link-${l.id}`}
              onClick={close}
              className="group relative whitespace-nowrap font-mono text-sm font-medium uppercase tracking-[0.05em] text-mist transition-colors hover:text-flame"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-flame transition-all duration-300 group-hover:w-full" />
            </Link>
          ))}

          <div className="group relative">
            <button
              data-testid="nav-contact-dropdown"
              className="flex items-center gap-1.5 font-mono text-sm font-medium uppercase tracking-[0.05em] text-mist transition-colors hover:text-flame"
            >
              Contact
              <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <div className="pointer-events-none absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 translate-y-2 pt-4 opacity-0 transition-all duration-300 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100">
              <div className="glass rounded-2xl border border-slate-200 p-2">
                <Link
                  to="/contact"
                  data-testid="nav-contact-page"
                  onClick={close}
                  className="flex items-start gap-3 rounded-xl px-4 py-3 transition-colors hover:bg-slate-50"
                >
                  <Mail className="mt-0.5 h-4 w-4 shrink-0 text-flame" />
                  <span>
                    <span className="block text-base font-semibold text-slate-900">
                      Contact
                    </span>
                    <span className="mt-0.5 block text-[15px] text-faint">
                      Send a message — a human answers
                    </span>
                  </span>
                </Link>
              </div>
            </div>
          </div>

          <button
            data-testid="nav-cta-button"
            onClick={goSection("book")}
            className="group flex items-center gap-1.5 whitespace-nowrap rounded-2xl bg-flame px-3.5 py-1.5 font-mono text-sm font-semibold uppercase tracking-[0.05em] text-obsidian transition-all duration-300 hover:shadow-[0_0_28px_rgba(255,85,0,0.45)]"
          >
            Free call
            <ArrowUpRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>

        <button
          data-testid="mobile-menu-button"
          aria-label="Toggle menu"
          onClick={() => setOpen(!open)}
          className="flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-coal/70 md:hidden"
        >
          <div className="relative h-3 w-4">
            <span
              className={`absolute left-0 top-0 h-0.5 w-full bg-white transition-all duration-300 ${
                open ? "top-1.5 rotate-45" : ""
              }`}
            />
            <span
              className={`absolute left-0 top-1.5 h-0.5 w-full bg-white transition-all duration-300 ${
                open ? "opacity-0" : ""
              }`}
            />
            <span
              className={`absolute left-0 top-3 h-0.5 w-full bg-white transition-all duration-300 ${
                open ? "top-1.5 -rotate-45" : ""
              }`}
            />
          </div>
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="glass mx-4 mt-3 max-h-[calc(100vh-6rem)] overflow-y-auto rounded-2xl border border-slate-200 p-6 md:hidden"
          >
            <div className="font-mono text-[13px] uppercase tracking-[0.25em] text-faint">
              About
            </div>
            <div className="mt-2 grid grid-cols-1 gap-1">
              <Link
                to="/about"
                data-testid="mobile-about-story"
                onClick={close}
                className="rounded-xl px-4 py-2.5 text-base font-semibold text-slate-700 hover:bg-slate-50"
              >
                Our Story
              </Link>
              <Link
                to="/todd"
                data-testid="mobile-about-todd"
                onClick={close}
                className="rounded-xl px-4 py-2.5 text-base font-semibold text-slate-700 hover:bg-slate-50"
              >
                Todd
              </Link>
            </div>
            <div className="mt-5 font-mono text-[13px] uppercase tracking-[0.25em] text-faint">
              Services
            </div>
            <div className="mt-2 grid grid-cols-1 gap-1">
              {SERVICES.map((s) => (
                <Link
                  key={s.slug}
                  to={s.route || `/services/${s.slug}`}
                  data-testid={`mobile-service-${s.slug}`}
                  onClick={close}
                  className="rounded-xl px-4 py-2.5 text-base font-semibold text-slate-700 hover:bg-slate-50"
                >
                  {s.title}
                </Link>
              ))}
            </div>
            <div className="mt-5 font-mono text-[13px] uppercase tracking-[0.25em] text-faint">
              Results
            </div>
            <div className="mt-2 grid grid-cols-1 gap-1">
              <button
                data-testid="mobile-results-all"
                onClick={goSection("results")}
                className="rounded-xl px-4 py-2.5 text-left text-base font-semibold text-slate-700 hover:bg-slate-50"
              >
                All Results
              </button>
              <Link
                to="/testimonials"
                data-testid="mobile-results-testimonials"
                onClick={close}
                className="rounded-xl px-4 py-2.5 text-base font-semibold text-slate-700 hover:bg-slate-50"
              >
                Client Testimonials
              </Link>
              <Link
                to="/case-studies"
                data-testid="mobile-case-studies-all"
                onClick={close}
                className="rounded-xl bg-flame/5 px-4 py-2.5 text-base font-semibold text-flame hover:bg-flame/10"
              >
                All Case Studies
              </Link>
              {CASES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/case-studies/${c.slug}`}
                  data-testid={`mobile-case-study-${c.slug}`}
                  onClick={close}
                  className="rounded-xl px-4 py-2.5 hover:bg-slate-50"
                >
                  <span className="block text-base font-semibold text-slate-700">
                    {c.client}
                  </span>
                  <span className="mt-0.5 block text-[13px] text-faint">
                    {c.industry}
                  </span>
                </Link>
              ))}
            </div>
            <nav className="mt-5 flex flex-col gap-1 border-t border-slate-200 pt-5">
              {MOBILE_MAIN.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35 }}
                >
                  <Link
                    to={l.route}
                    data-testid={`mobile-link-${l.id}`}
                    onClick={close}
                    className="block rounded-xl px-4 py-3 font-display text-xl font-bold text-slate-900 hover:bg-slate-50"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
